import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Wheat, CakeSlice, Citrus, Soup, Gift, CalendarHeart, ArrowRight } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

const CATEGORIES = ["Tutti", "Pasta fresca", "Dolci", "Conserve", "Salse", "Box regalo", "Stagionali"];

const PRODUCTS = [
  { id: "tagliatelle", cat: "Pasta fresca", name: "Tagliatelle fresche", desc: "Sfoglia tirata a mano con uova e semola. Quelle della domenica, ma quando vuoi tu.", icon: Wheat },
  { id: "ravioli", cat: "Pasta fresca", name: "Ravioli di stagione", desc: "Ripieni che seguono il mercato: verdure, ricotta, erbe. La ricetta cambia, la cura no.", icon: Wheat },
  { id: "gnocchi", cat: "Pasta fresca", name: "Gnocchi di patate", desc: "Morbidi, fatti il giorno stesso. Da condire forte, mi raccomando.", icon: Wheat },
  { id: "torta-bottega", cat: "Dolci", name: "Torta della bottega", desc: "La torta del momento: cambia con la stagione e con quello che ci ispira. Chiedi quale c'è.", icon: CakeSlice },
  { id: "crostata", cat: "Dolci", name: "Crostata con confettura nostra", desc: "Frolla friabile e confettura fatta da noi. Semplice, nel senso buono.", icon: CakeSlice },
  { id: "biscotti", cat: "Dolci", name: "Biscotti da inzuppo", desc: "Per il caffè, per il tè, per le undici di sera. Non giudichiamo.", icon: CakeSlice },
  { id: "confettura", cat: "Conserve", name: "Confettura di stagione", desc: "Frutta, zucchero, pazienza. Il gusto dipende da cosa porta il mercato.", icon: Citrus },
  { id: "sottoli", cat: "Conserve", name: "Verdure sott'olio", desc: "Verdure dell'orto conservate come si faceva una volta. Perché funziona.", icon: Citrus },
  { id: "ragu-vegetale", cat: "Salse", name: "Ragù vegetale", desc: "Lento, ricco, convinto. Anche i carnivori chiedono il bis.", icon: Soup },
  { id: "sugo-giorno", cat: "Salse", name: "Sugo del giorno", desc: "Quello che passa la bottega, in vasetto. Segui i nostri canali per sapere qual è.", icon: Soup },
  { id: "box-regalo", cat: "Box regalo", name: "Box regalo", desc: "Una selezione di cose buone da regalare. La componiamo insieme, in base a chi la riceve.", icon: Gift },
  { id: "box-stagionale", cat: "Stagionali", name: "Box di stagione", desc: "Il meglio del momento in una scatola: pasta, conserve, qualcosa di dolce.", icon: CalendarHeart },
];

export default function Bottega() {
  const [cat, setCat] = useState("Tutti");
  const visible = cat === "Tutti" ? PRODUCTS : PRODUCTS.filter((p) => p.cat === cat);

  useSeo({
    title: "La Bottega | Prodotti artigianali fatti a mano a Rimini",
    description: "Pasta fresca, dolci, conserve, salse e box regalo fatti a mano a Rimini. Piccoli lotti, ingredienti di stagione, da ordinare e portare a casa.",
    path: "/bottega",
  });

  return (
    <>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-12 sm:pt-20 sm:pb-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-testid="bottega-hero">
        <motion.div className="lg:col-span-6" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: "easeOut" }}>
          <p className="text-xs uppercase tracking-[0.25em] text-terracotta font-semibold mb-5">La Bottega</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.1] text-ink">
            Cose fatte a mano, <em className="text-forest">da portare a casa.</em>
          </h1>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-stone-600 max-w-xl">
            Pasta fresca, dolci, conserve e salse: tutto prodotto in piccoli lotti, con ingredienti
            di stagione e una certa testardaggine. Si ordina, si ritira, si mangia.
          </p>
          <p className="mt-4 text-sm text-stone-500 max-w-xl italic">
            Disponibilità e produzione cambiano in base alla stagione.
          </p>
        </motion.div>
        <motion.div className="lg:col-span-6" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }}>
          <div className="rounded-3xl overflow-hidden shadow-xl shadow-forest/10 border border-stone-200/60">
            <img
              src="/images/pasta-nest.jpg"
              alt="Nidi di pasta fresca fatta a mano su un piano infarinato"
              className="w-full h-72 sm:h-96 object-cover"
              fetchPriority="high"
              data-testid="bottega-hero-image"
            />
          </div>
        </motion.div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 sm:pb-28" data-testid="bottega-catalog">
        <div className="flex flex-wrap gap-2.5" role="group" aria-label="Filtra per categoria">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              aria-pressed={cat === c}
              data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-all active:scale-95 ${
                cat === c
                  ? "bg-forest text-cream border-forest"
                  : "bg-white/70 text-stone-700 border-stone-300 hover:border-forest hover:text-forest"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {visible.map((p, i) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              className="bg-white/80 border border-stone-200/80 rounded-2xl overflow-hidden shadow-sm hover:shadow-md hover:border-forest/30 hover:-translate-y-1 transition-all flex flex-col"
              data-testid={`product-card-${p.id}`}
            >
              <div className="h-40 bg-clay flex items-center justify-center" role="img" aria-label={`Immagine in arrivo: ${p.name}`}>
                <p.icon size={44} className="text-forest/40" aria-hidden="true" />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <p className="text-xs uppercase tracking-[0.18em] text-terracotta font-semibold">{p.cat}</p>
                <h2 className="font-display text-xl mt-2 text-ink">{p.name}</h2>
                <p className="mt-2 text-sm leading-relaxed text-stone-600 flex-1">{p.desc}</p>
                <Link
                  to={`/contatti?prodotto=${encodeURIComponent(p.name)}`}
                  data-testid={`product-inquire-${p.id}`}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-forest hover:text-terracotta transition-colors w-fit"
                >
                  Richiedi informazioni <ArrowRight size={15} aria-hidden="true" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>

        <motion.p {...fadeUp} className="mt-12 text-sm text-stone-500 max-w-2xl">
          Non trovi quello che cercavi? Scrivici: se si può fare in una piccola cucina, probabilmente lo facciamo.
        </motion.p>
      </section>
    </>
  );
}
